import React, { useEffect } from 'react';
import CSS from 'csstype';
import '../../App.css';
import '../../tools/search-results/search-results.css';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// images
import { Square, SquareCheckBig } from 'lucide-react';

// types

// types specifically for this component.
import { SelectedRowsType } from './search-catalog-types';

// functions
import {
  extractFileFromAccessUrl,
  extractFileFromObsPublisherDid,
} from '../../utils/functions';

// classes
import PleaseWait from '../../tools/please-wait';

import { Event, EventType, EventTarget, ExecuteADQLQuery } from '../../utils/events';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

// types specifically for this component.
import { Table } from 'react-bootstrap';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import { rowCheckedHandler, setSelectAll } from './search-catalog-slice';
import { checkSelectAll, getFieldValue } from './search-catalog-actions';
import { handleEvent } from '../../app-actions';
import { useNavigate } from 'react-router-dom';

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function SearchResultsTable(props: { key: string }) {
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state variables.
  const sG_DarkMode: boolean = useAppSelector((state) => state.global.darkMode);

  // LOCAL STATE VARIABLES:

  // maintain the state for the selected rows on the results tabs.
  const sSelectedRows: SelectedRowsType[] = useAppSelector(
    (state) => state.searchCatalog.selectedItems.selectedRows,
  );

  // the retrieved data from the TAP server.
  const sTapData: any[] = useAppSelector((state) => state.searchCatalog.tapData);
  const sLoadingTapData: boolean = useAppSelector(
    (state) => state.searchCatalog.loadingTapData,
  );
  const sColumns: string[] = useAppSelector((state) => state.searchCatalog.columns);
  const sErrorMessage: string = useAppSelector(
    (state) => state.searchCatalog.errorMessage,
  );
  const sSelectAll: boolean = useAppSelector(
    (state) => state.searchCatalog.selectedItems.selectAll,
  );

  // page handling.
  const sPage: number = useAppSelector((state) => state.searchCatalog.page);
  const sPageSize: number = useAppSelector((state) => state.searchCatalog.pageSize);

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // class-level constants.
  const TABLE_COL_GENERAL: CSS.Properties = {
    whiteSpace: 'nowrap',
    border: 'none',
    color: sG_DarkMode === true ? 'white' : 'black',
    padding: '10px 4px 10px 4px',
  };
  const TABLE_COL: CSS.Properties = {
    ...TABLE_COL_GENERAL,
    ...{
      background: 'transparent',
    },
  };
  const TABLE_COL_HEADER: CSS.Properties = {
    border: 'none',
    position: 'sticky',
    zIndex: '1',
    backgroundColor: 'var(--window-background)',
    top: '0',
    alignItems: 'center',
    padding: '0px 10px 10px 4px',
  };

  //	------------------------------------------------------------
  //
  //	Button handler for the select all checkbox.
  //
  //	------------------------------------------------------------

  const checkChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    // invert the select all check box.
    const selectAll = !sSelectAll;
    dispatch(setSelectAll(selectAll));

    // get the list of selected rows from state.
    var rows: SelectedRowsType[] = [];
    var ticked: boolean[] = [];

    // loop over all the rows, adding them to the selected-items list..
    for (var i: number = 0; i < sTapData.length; i++) {
      //
      var newItem: SelectedRowsType = getValuesFromSourceList({ item: sTapData[i] });

      // extract namespace and name.
      var fileDetails: {
        namespace: string;
        filename: string;
      } = extractFileFromObsPublisherDid({ obsPublisherDid: newItem.obsPublisherDid });
      if (fileDetails.namespace === '' && newItem.accessUrl !== undefined) {
        fileDetails = extractFileFromAccessUrl({ accessUrl: newItem.accessUrl });
      }
      newItem.namespace = fileDetails.namespace;
      newItem.filename = fileDetails.filename;

      rows.push(newItem);
      ticked.push(selectAll === true);
      //
    }

    // update state.
    dispatch(rowCheckedHandler({ rows: rows, ticked: ticked }));
    //
  }; // checkChange

  //	------------------------------------------------------------
  //
  //	Copies a string value into the clipboard
  //
  //	------------------------------------------------------------

  const copyToClipboard = (str: string) => {
    const el = document.createElement('textarea');
    el.value = str;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    const getSelection = document.getSelection();
    if (getSelection !== null) {
      const selected = getSelection.rangeCount > 0 ? getSelection.getRangeAt(0) : false;
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      if (selected) {
        getSelection.removeAllRanges();
        getSelection.addRange(selected);
      }
    }
  }; // copyToClipboard

  //	------------------------------------------------------------
  //
  //	Get some information and download a file.
  //
  //	------------------------------------------------------------

  function downloadFile(args: { row: number }) {
    //
    // get the row that is to be retrieved.
    var row: any = sTapData[args.row];

    // get the obs_publisher_did and the access_url.
    const obsPublisherDid: string = getFieldValue({
      fieldName: 'obs_publisher_did',
      item: row,
    });
    const accessUrl: string = getFieldValue({
      fieldName: 'access_url',
      item: row,
    });

    // get the RA and Dec.
    var ra: string = '';
    var raColID: number = sColumns.findIndex((element) => element === 's_ra');
    if (raColID > -1) ra = row[sColumns[raColID]];
    var dec: string = '';
    var decColID: number = sColumns.findIndex((element) => element === 's_dec');
    if (decColID > -1) dec = row[sColumns[decColID]];

    // extract namespace and name.
    var fileDetails: {
      namespace: string;
      filename: string;
    } = extractFileFromObsPublisherDid({ obsPublisherDid: obsPublisherDid });
    if (fileDetails.namespace === '') {
      fileDetails = extractFileFromAccessUrl({ accessUrl: accessUrl });
    }

    // download the XML file.
    fetch(accessUrl)
      .then((response) => response.text())
      .then((responseText) => {
        // parse the XML.
        const parser = new DOMParser();
        var xml: any = parser.parseFromString(responseText, 'text/xml');

        // move to VOTABLE.
        var node: any = undefined;
        var idx: number = -1;
        [node, idx] = findChild({ node: xml, nodeName: 'VOTABLE' });

        // find the SODA-sync resource child.
        var sodaSyncNode: any = undefined;
        [sodaSyncNode, idx] = findChild({
          node: node,
          nodeName: 'RESOURCE',
          attributeName: 'ID',
          attributeValue: 'soda-sync',
        });

        // move to RESOURCE -> TABLE.
        [node, idx] = findChild({ node: node, nodeName: 'RESOURCE' });
        [node, idx] = findChild({ node: node, nodeName: 'TABLE' });

        // find the child node with a nodeName of FIELD and an attribute 'name' = 'ID'.
        var idNode: any = undefined;
        var idIndex: number = -1;
        [idNode, idIndex] = findChild({
          node: node,
          nodeName: 'FIELD',
          attributeName: 'name',
          attributeValue: 'ID',
        });

        // move to DATA -> TABLEDATA -> TR.
        [node, idx] = findChild({ node: node, nodeName: 'DATA' });
        [node, idx] = findChild({ node: node, nodeName: 'TABLEDATA' });
        [node, idx] = findChild({ node: node, nodeName: 'TR' });

        // find the nth item with node name "TD".
        [idNode, idx] = findChild({ node: node, nodeName: 'TD', nthItem: idIndex });
        var ivoLink: string = '';
        if (idNode !== undefined) ivoLink = idNode['textContent'];

        // extract the file name from the ID column (everything after the last /).
        var filename: string = '';
        if (ivoLink.lastIndexOf('/') > -1)
          filename = ivoLink.slice(ivoLink.lastIndexOf('/') + 1 - ivoLink.length);

        // extract the Rucio namespace from the ID column (everything after the ? and before the next /).
        var namespace: string = '';
        if (ivoLink.indexOf('?') > -1) {
          namespace = ivoLink.slice(ivoLink.indexOf('?') - ivoLink.length + 1);
          if (namespace.indexOf('/') > -1)
            namespace = namespace.slice(0, namespace.indexOf('/'));
          else namespace = '';
        }

        // Get the access url from the SODA-sync resource.
        var accessUrl: string = '';
        if (sodaSyncNode !== undefined) {
          var accessNode: any = undefined;
          [accessNode, idx] = findChild({
            node: sodaSyncNode,
            nodeName: 'PARAM',
            attributeName: 'name',
            attributeValue: 'accessURL',
          });
          if (accessNode !== undefined) {
            accessNode = findAttribute({ node: accessNode, attributeName: 'value' });
            if (accessNode !== undefined) accessUrl = accessNode['textContent'];
          }
        }

        // strip the domain from the url.
        /*var apiAddress: string = '';
        if (accessUrl.indexOf('.int/') > -1)
          apiAddress = accessUrl.slice(accessUrl.indexOf('.int/') + 4 - accessUrl.length);*/

        // exchange the data-management token for a download token.

        // fetch the file.
        if (
          accessUrl !== '' &&
          fileDetails.namespace !== '' &&
          filename !== '' &&
          ivoLink !== ''
        ) {
          // append the ivoLink to the access URL.
          accessUrl = accessUrl + '?ID=' + ivoLink;

          // append the circle to the access URL.
          if (ra !== '' && dec !== '')
            accessUrl = accessUrl + '&CIRCLE=' + ra + '%20' + dec + '%2060';
          copyToClipboard(accessUrl);

          /*const authorizationHeader: string = 'Authorization: Bearer ' + this.props.accessToken;
						fetch( apiAddress, { method: 'GET', headers: { 'Content-Type': 'application/fits' } } )
							.then
							(
								(response) =>
									response.blob()
									
							)
							.then
							(
								(blob) =>
								{
								
									// create blob link to download.
									const url = window.URL.createObjectURL( new Blob( [blob] ) );
									const link = document.createElement( 'a' );
									link.href = url;
									link.setAttribute( 'download', filename );

									// append to html link element page.
									document.body.appendChild( link );

									// start download.
									link.click();

									// clean up and remove the link.
									if (link.parentNode != null)
										link.parentNode.removeChild( link );
								}
							);*/
        }
      })
      .catch((error) => {
        console.log('error fetching the XML file: ', error);
      });
  } // downloadFile

  //	------------------------------------------------------------
  //
  //	Download a file from the LoTTS database.
  //
  //	------------------------------------------------------------
  /*	
	downloadFileLoTTS( args: { row: number } )
	{
	
		// get the row that is to be retrieved.
		var row: any = this.state.sourceList[ args.row ];
		
		// get the result (url).
		var result: string = "";
		var resultColID: number = this.state.columns.findIndex( element => element === 'result' )
		if (resultColID > -1)
			result = row[ this.state.columns[ resultColID ] ];
			
		// have we retrieved the result field?
		if (result != '')
		{
		
			// extract filename part (everything after the last '/'.
			var filename: string = '';
			if (result.lastIndexOf( '/' ) > -1)
				filename = result.slice( result.lastIndexOf( '/' ) + 1 - result.length );
			
			// strip the domain from the url.
			var apiAddress: string = '';
			if (result.indexOf( '.nl/' ) > -1)
				apiAddress = result.slice( result.indexOf( '.nl/' ) + 3 - result.length );
		
			fetch( apiAddress, { method: 'GET', headers: { 'Content-Type': 'application/fits' } } )
				.then
				(
					(response) =>
						response.blob()
				)
				.then
				(
					(blob) =>
					{
					
						// create blob link to download.
						const url = window.URL.createObjectURL( new Blob( [blob] ) );
						const link = document.createElement( 'a' );
						link.href = url;
						link.setAttribute( 'download', filename );

						// append to html link element page.
						document.body.appendChild( link );

						// start download.
						link.click();

						// clean up and remove the link.
						if (link.parentNode != null)
							link.parentNode.removeChild( link );
					}
				);
		
		}
	
	} // downloadFileLoTTS
*/
  //	------------------------------------------------------------
  //
  //	When given an XML node, look for an attribute with a given
  //	name matching a given value.
  //
  //	------------------------------------------------------------

  function findAttribute(args: {
    node: any;
    attributeName: string;
    attributeValue?: string;
  }) {
    // loop through the elements attached to this node to see if we can find one matching the required value.
    var returnNode: any = undefined;
    for (let i = 0; i < args.node['attributes'].length; i++) {
      var attribute: any = args.node['attributes'][i];
      if (attribute['nodeName'].toUpperCase() === args.attributeName.toUpperCase()) {
        var attributeValueOK: boolean = true;
        if (args.attributeValue !== undefined)
          attributeValueOK =
            attribute['nodeValue'].toUpperCase() === args.attributeValue.toUpperCase();
        if (attributeValueOK === true) returnNode = attribute;
      }
    }

    return returnNode;
  } // findAttribute

  //	------------------------------------------------------------
  //
  //	When given an XML node, look for a child with a given node name.
  //
  //	------------------------------------------------------------

  function findChild(args: {
    node: any;
    nodeName: string;
    attributeName?: string;
    attributeValue?: string;
    nthItem?: number;
  }) {
    var childIndex: number = -1;
    var elementCount: number = -1;
    var elementID: number = -1;

    if (args.node !== undefined)
      args.node['childNodes'].forEach((element: any, index: number) => {
        if (
          element['nodeName'].toUpperCase() === args.nodeName.toUpperCase() &&
          childIndex === -1
        ) {
          // look for the required attribute.
          var attributeMissing: boolean = false;
          if (
            args.attributeName !== undefined &&
            args.attributeValue !== undefined &&
            'attributes' in element
          ) {
            attributeMissing = true;
            const attribute = findAttribute({
              node: element,
              attributeName: args.attributeName,
              attributeValue: args.attributeValue,
            });
            if (attribute !== undefined) attributeMissing = false;
          }

          // increment the element count.
          if (attributeMissing === false) elementCount = elementCount + 1;

          // if we specifically need the nth item, then check if that's what we've found.
          var nthItemFound: boolean = true;
          if (args.nthItem !== undefined) nthItemFound = elementCount === args.nthItem;

          // are we happy ?
          if (attributeMissing === false && nthItemFound === true) {
            childIndex = index;
            elementID = elementCount;
          }
        }
      });
    if (childIndex > -1) return [args.node['childNodes'][childIndex], elementID];
    else return [undefined, -1];
  } // findChild

  //	------------------------------------------------------------
  //
  //	Populate the values we need for a selected-rows object.
  //
  //	------------------------------------------------------------

  function getValuesFromSourceList(args: { item: any }) {
    //
    var values: SelectedRowsType = {
      namespace: '',
      filename: '',
      obsPublisherDid: '',
      accessUrl: undefined,
      targetName: undefined,
      obsID: undefined,
      dataProductType: undefined,
      facilityName: undefined,
      sRA: undefined,
      sDec: undefined,
    };

    // get some values from the source list.
    if ('obs_publisher_did' in args.item) {
      values.obsPublisherDid = args.item['obs_publisher_did'];
    }
    if ('access_url' in args.item) {
      values.accessUrl = args.item['access_url'];
    }
    if ('target_name' in args.item) {
      values.targetName = args.item['target_name'];
    }
    if ('obs_id' in args.item) {
      values.obsID = args.item['obs_id'];
    }
    if ('dataproduct_type' in args.item) {
      values.dataProductType = args.item['dataproduct_type'];
    }
    if ('facility_name' in args.item) {
      values.facilityName = args.item['facility_name'];
    }
    if ('s_ra' in args.item) {
      if (typeof args.item['s_ra'] === 'number') {
        values.sRA = args.item['s_ra'];
      }
    }
    if ('s_dec' in args.item) {
      if (typeof args.item['s_dec'] === 'number') {
        values.sDec = args.item['s_dec'];
      }
    }

    // return something.
    return values;
    //
  } // getValuesFromSourceList

  //	------------------------------------------------------------
  //
  //	Button handler for check box onChange events.
  //
  //	------------------------------------------------------------

  const rowCheckChange = (event: React.MouseEvent<HTMLButtonElement>, pRowID: number) => {
    //
    var newItem: SelectedRowsType = getValuesFromSourceList({
      item: sTapData[pRowID],
    });

    // extract namespace and name.
    var fileDetails: {
      namespace: string;
      filename: string;
    } = extractFileFromObsPublisherDid({ obsPublisherDid: newItem.obsPublisherDid });
    if (fileDetails.namespace === '' && newItem.accessUrl !== undefined) {
      fileDetails = extractFileFromAccessUrl({ accessUrl: newItem.accessUrl });
    }
    newItem.namespace = fileDetails.namespace;
    newItem.filename = fileDetails.filename;

    var pos: number = 0;
    if ('obsPublisherDid' in newItem) {
      pos = sSelectedRows.findIndex(
        (element) => element.obsPublisherDid === newItem.obsPublisherDid,
      );
    }
    const ticked: boolean = pos === -1;

    // raise an onChange event.
    dispatch(rowCheckedHandler({ rows: [newItem], ticked: [ticked] }));
    //
  }; // rowCheckChange

  //	------------------------------------------------------------
  //
  //	Add a function to handle collection name clicks
  //
  //	------------------------------------------------------------

  function handleCollectionClick(collectionName: string) {
    //
    // Raise a new ADQL query
    const adqlQuery = `SELECT * FROM ivoa.ObsCore WHERE obs_collection = '${collectionName}'`;

    const params: ExecuteADQLQuery = {
      adqlQuery: adqlQuery,
    };

    const newEvent: Event = {
      eventTarget: EventTarget.SEARCH_CATALOG_FORM,
      eventType: EventType.EXECUTE_ADQL_QUERY,
      parameters: params,
    };

    dispatch(
      handleEvent({
        event: newEvent,
      }),
    );
    //
  }

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T S
  //
  //	------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Constructs a table header row from a list of column names.
  //
  //	------------------------------------------------------------

  function TableHeadings() {
    return (
      <>
        {sColumns.map((column: string) => {
          return (
            <th
              style={{ ...TABLE_COL, ...TABLE_COL_HEADER }}
              data-hidden="true"
              key={column}
            >
              {column}
            </th>
          );
        })}
      </>
    );
  } // TableHeadings

  //	------------------------------------------------------------
  //
  //	A HTML component that takes a list of sources and renders
  //	them as a list of table rows.
  //
  //	------------------------------------------------------------

  function TableRows(args: {
    sourceList: any[];
    startIndex: number;
    onChange: any;
    selectedRows: SelectedRowsType[];
  }) {
    try {
      return (
        <>
          {args.sourceList.map((item, index) => (
            <tr className="search-results-table-row" style={{ border: 'none' }}>
              <td
                style={{
                  ...TABLE_COL,
                }}
              >
                <button
                  type="button"
                  title="Select/deselect row"
                  className="action-button text-colour"
                  onClick={(event) =>
                    rowCheckChange(/* event = */ event, /* pRowID = */ index)
                  }
                  style={{
                    height: '22px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    background: 'transparent',
                    border: 'none',
                  }}
                >
                  {args.selectedRows.findIndex(
                    (element) =>
                      element.obsPublisherDid ===
                      getFieldValue({ fieldName: 'obs_publisher_did', item: item }),
                  ) > -1 ? (
                    <SquareCheckBig width={25} height={25} strokeWidth={1.5} />
                  ) : (
                    <Square width={25} height={25} strokeWidth={1.5} />
                  )}
                </button>
              </td>
              {sColumns.map((column) => (
                <td
                  className="text-colour"
                  style={{
                    ...TABLE_COL,
                  }}
                >
                  {column === 'obs_collection' ? (
                    <span
                      className="link"
                      onClick={() => handleCollectionClick(item[column])}
                    >
                      {item[column]}
                    </span>
                  ) : (
                    <div className="text-colour">{item[column]}</div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </>
      );
    } catch (e) {
      console.error('Error rendering table rows:', e);
      return <></>;
    }
  } // TableRows

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	re-check if all rows are selected if the list of selected
  //	rows changes.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log('[sSelectedRows] hook fires [search-results-table]');
    const allSelected: boolean = checkSelectAll({
      sourceList: sTapData,
      selectedRows: sSelectedRows,
      columns: sColumns,
    });
    dispatch(setSelectAll(allSelected));
    //
  }, [sSelectedRows]);

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	--------------------------------------------------------------------------

  /* render a table row with this source */
  return (
    <div className="results-scroll">
      <Table style={{ display: sLoadingTapData ? 'none' : 'table' }}>
        <thead>
          <tr className="search-results-table-row-header">
            <th style={{ ...TABLE_COL, ...TABLE_COL_HEADER }}>
              <button
                name="select-all-rows"
                type="button"
                title="Select/deselect all"
                className="action-button text-colour"
                onClick={checkChange}
              >
                {sSelectAll === true ? (
                  <SquareCheckBig width={25} height={25} strokeWidth={1.5} />
                ) : (
                  <Square width={25} height={25} strokeWidth={1.5} />
                )}
              </button>
            </th>
            {TableHeadings()}
          </tr>
        </thead>
        <tbody>
          {TableRows({
            sourceList: sTapData,
            startIndex: sPage * sPageSize,
            onChange: rowCheckChange,
            selectedRows: sSelectedRows,
          })}
        </tbody>
      </Table>
      <PleaseWait text="Loading" visible={sLoadingTapData === true} />
      <div
        className="text-colour"
        style={{
          display: sErrorMessage ? 'flex' : 'none',
          flexDirection: 'row',
          alignItems: 'center',
          margin: '10px',
          padding: '10px',
          backgroundColor: 'rgba(255, 0, 0, 0.1)',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          borderRadius: '4px',
        }}
      >
        <div
          style={{
            marginRight: '10px',
            fontSize: '20px',
            flex: '0 0 auto',
          }}
        >
          ⚠️
        </div>
        <div
          style={{
            flex: '1 1',
          }}
        >
          {sErrorMessage}
        </div>
      </div>
    </div>
  );
} // SearchResultsTable
